/**
 * Vault grade display — BETA-COMMERCE-298.
 *
 * ⛔⛔ THIS EXISTS BECAUSE THE VAULT READ TWO FIELDS THE API HAS NEVER SERVED.
 * `CollectionList.jsx` and `ItemDetail.jsx` both did:
 *
 *     const grade = item.grade || item.numeric_grade;
 *
 * `CollectionItemResponse` (app/schemas/collection.py) has NEITHER key. Verified against the live
 * schema: `grade` False, `numeric_grade` False, `user_grade` True, `display_grade` True. So `grade`
 * was `undefined` for every real vault item, `{grade && (...)}` short-circuited, and the grade badge
 * NEVER RENDERED — on a screen whose entire job is showing you your graded books.
 *
 * It looked wired. The tests passed. They passed because they hand-fed `{grade: '9.4'}` fixtures —
 * a shape the backend does not produce — so the suite proved the component could render a field
 * nobody sends. Contract drift, invisible from either side alone.
 *
 * The alias keys are still accepted: they cost nothing, and some callers construct items locally.
 * But `user_grade`/`display_grade` are the TRUTH, and they are consulted first.
 */

import { CGC_GRADE_SCALE } from './GradeRecalculator';

/** The server's sentinel for "no grade" (user_collection.py display_grade). Not a grade. */
export const UNGRADED_SENTINEL = 'Ungraded';

/**
 * V-SCHEMA-FE: THE ONE grade list, as the strings `user_grade` stores.
 *
 * Three hand-typed lists existed: QuickAdd had `9.5` (not a CGC grade) and lacked
 * 9.2/9.4/9.6/9.8 -- the grades most slabbed moderns actually carry; ItemDetail lacked
 * 1.8/9.9; VaultDetailPane was a free-text input. This is the string form of the
 * canonical CGC_GRADE_SCALE that GradeRecalculator snaps to, so a grade offered here is a
 * grade the recalculator can produce and vice versa.
 */
export const GRADE_OPTIONS = Object.freeze(CGC_GRADE_SCALE.map((g) => g.toFixed(1)));

/**
 * The cover image URL to show for a vault item, or null.
 *
 * CollectionItemResponse serves `cover_photo_url` (the user's own photo) and, once the
 * list route hydrates it, `cover_url` (resolved) / `cover_image` (the linked issue's
 * catalog cover on GET /vault/items/{id}). Callers had `cover_url || cover_image` and
 * never looked at the one field the schema had all along.
 *
 * @param {Object|null|undefined} item
 * @returns {string|null}
 */
export function vaultItemCover(item) {
  if (!item) return null;
  return item.cover_url ?? item.cover_photo_url ?? item.cover_image ?? null;
}

/**
 * VAULT-S2b OP-2 RELEASED: the served `value_source` vocabulary -> the caption the UI shows.
 *
 * CollectionItemResponse.value_source is one of market_cache | catalog_slabbed |
 * catalog_raw | purchase | none. The two catalog rungs are ONE caption ("catalog"): which
 * catalog column fed the number is the server's ladder detail, not something the collector
 * acts on. `none` (and anything unknown) has no caption.
 */
export const VALUE_SOURCE_LABELS = Object.freeze({
  market_cache: 'market',
  catalog_slabbed: 'catalog',
  catalog_raw: 'catalog',
  purchase: 'purchase',
});

/** A served Decimal ("75.00"), a number, or nothing -> a finite number or null. */
function toFiniteOrNull(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * The value to DISPLAY for a vault item, or null when the server has none.
 *
 * READ-ONLY, DERIVED. VAULT-S2b OP-2 RELEASED: `estimated_value` is computed by the server
 * at read time from the ONE pricing ladder (fresh market_value cache -> linked catalog
 * slabbed/raw price -> purchase_price) and is the ONLY field read. There is deliberately NO
 * client-side fallback to the served `market_value`: the ladder already consulted that cache
 * behind its 30-day freshness gate, so the only served row with a null `estimated_value` and
 * a populated `market_value` is a STALE cache the server refused -- and `CollectionStats.
 * total_value` / GET /vault/valuation (both SUM(estimated_value)) exclude that book. A second
 * ladder here would price a book the totals do not. `current_value` is RETIRED -- never
 * written, never read here; a client that sends it 422s.
 *
 * Decimals arrive as strings ("75.00"); the result is a number so every consumer formats
 * and compares the same thing. A null renders as "—", never as $0 -- zero is a price,
 * null is "we do not know".
 *
 * @param {Object|null|undefined} item
 * @returns {number|null}
 */
export function vaultItemValue(item) {
  if (!item) return null;
  return toFiniteOrNull(item.estimated_value);
}

/**
 * The caption naming where the DISPLAYED value came from: 'market' | 'catalog' |
 * 'purchase', or null when there is no value (or the server named no rung for it).
 *
 * Follows vaultItemValue exactly: the caption describes the served `estimated_value` and
 * nothing else -- a row with no value has no caption, whatever `market_value` holds.
 *
 * @param {Object|null|undefined} item
 * @returns {string|null}
 */
export function vaultItemValueSource(item) {
  if (vaultItemValue(item) === null) return null;
  return VALUE_SOURCE_LABELS[item.value_source] ?? null;
}

/**
 * The server's FINISHED caption for the displayed value -- "based on VF", "based on CGC 9.8",
 * "no grade on file; VF catalog price", "loose-copy price, not a grade band" -- or null when
 * there is nothing to caption.
 *
 * ⛔ THE CLIENT DOES NOT COMPOSE THIS AND MUST NOT. Read the strings above again: which one a row
 * gets depends on whether the BOOK's own grade selected the band (`based on FN` for a graded 7.0
 * that fell through to price_fn) or the CATALOG merely happened to hold only that row (`no grade
 * on file; VF catalog price` for an ungraded book), and `price_loose` is captioned as not-a-band
 * at all because it is on a different scale from the grade bands. That decision needs the item's
 * grade, the resolved rung and the rung table together -- exactly the three things the server has
 * beside the number when it computes it. Re-deriving any part of it here means a second table on
 * the client, free to disagree with the first, and a caption that disagrees with its own number is
 * worse than no caption. This function's entire job is: is there a value, and is the served
 * caption a non-empty string.
 *
 * ⛔ WHY THIS IS NOT ANOTHER KEY ON VALUE_SOURCE_LABELS. The band is a SEPARATE AXIS from the
 * source. `value_source` answers WHICH LADDER produced the number and reads `catalog_raw` for
 * ALL FIVE raw bands plus the loose floor; this answers WHICH GRADE the number is the price of.
 * Both catalog ladders fall THROUGH to lower bands, so a row can legitimately serve a GD price --
 * and a bare "$2,443.50 / catalog" renders identically whether it came from `price_nm` or
 * `price_gd`. That is a number lying by omission about money.
 *
 * Follows vaultItemValue: no displayed value means no caption. The server serves null wherever
 * there is no band (the 30-day market_value cache, purchase_price, and the `none` floor), and a
 * null must render NOTHING AT ALL -- never an invented or defaulted basis, and never an empty
 * element, a stray separator or the word "undefined".
 *
 * @param {Object|null|undefined} item
 * @returns {string|null}
 */
export function vaultItemValueBasis(item) {
  if (vaultItemValue(item) === null) return null;
  const basis = item.value_basis;
  return typeof basis === 'string' && basis !== '' ? basis : null;
}

/**
 * The string to show on a grade badge, or null when the item genuinely has no grade.
 *
 * Prefers `display_grade`, which the server already composes as "CGC 9.4" for slabbed books —
 * except when it is the "Ungraded" sentinel, which must render as NO badge rather than as a badge
 * reading "Ungraded".
 *
 * @param {Object|null|undefined} item
 * @returns {string|null}
 */
export function vaultItemGrade(item) {
  if (!item) return null;

  const display = item.display_grade;
  if (typeof display === 'string' && display && display !== UNGRADED_SENTINEL) {
    return display;
  }

  const raw = item.user_grade ?? item.grade ?? item.numeric_grade;
  if (raw === null || raw === undefined || raw === '') return null;
  return String(raw);
}

/**
 * The numeric part of the grade, for badge TIER selection (9.8+ gold, 9.0+ silver, ...).
 *
 * ⚠ `parseFloat('CGC 9.4')` is NaN — the leading company name defeats it, so a slabbed 9.8 would
 * silently drop to the lowest tier. Pull the first number out of the string instead.
 *
 * @param {Object|null|undefined} item
 * @returns {number} the grade, or NaN when there is none
 */
export function vaultItemGradeNumber(item) {
  const label = vaultItemGrade(item);
  if (label === null) return NaN;
  const match = /-?\d+(?:\.\d+)?/.exec(label);
  return match ? parseFloat(match[0]) : NaN;
}

export default {
  UNGRADED_SENTINEL,
  GRADE_OPTIONS,
  VALUE_SOURCE_LABELS,
  vaultItemGrade,
  vaultItemGradeNumber,
  vaultItemCover,
  vaultItemValue,
  vaultItemValueSource,
  vaultItemValueBasis,
};
